import { CheckCircle2, ConciergeBell, DollarSign, Hotel, Plane, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Role } from '@nexa/auth';
import { Button } from '@/components/ui/button';
import { RoleGate } from '@/auth/role-gate';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { isPriced, UNPRICED_KEY } from '@/lib/allocation/price';
import { useAllocation } from './allocation-tab/use-allocation';
import { useCaseOverview } from './use-case-overview';
import { useConvertCareOnly } from './use-care-only';
import { useResolveCase } from './use-resolve-case';

// The case's numbers, in one line in the header — what the Overview tab used to
// spend a whole screen saying. The tab is gone: the work happens on the PNR rows
// and inside their operation-center drawer, so the case level only has to answer
// "how big is this, how far along is it, what does it come to" and offer Resolve.
//
// Two queries, both already fired by the tabs below (React Query dedups them):
// the case overview (status, pax) and the BFF allocation view — the ONE source
// for group / room / spend figures (architecture_case_detail_numbers_source_of_truth).
// Nothing is derived here that the server did not already compute.
//
// Every stat renders only when it has something true to say: a case with no
// hotel need shows no rooms stat rather than "0/0".

interface CaseStatStripProps {
  caseUrn: string;
}

/** `1234.5` → `US$1.2k`; below 1000 stays whole (`US$840`). */
function compactUsd(value: number): string {
  const rounded = Math.round(value);
  if (rounded < 1000) return `US$${rounded}`;
  return `US$${(rounded / 1000).toFixed(rounded < 10_000 ? 1 : 0)}k`;
}

export function CaseStatStrip({ caseUrn }: CaseStatStripProps) {
  const { t } = useTranslation('caseDetail');
  const overview = useCaseOverview(caseUrn);
  const allocation = useAllocation(caseUrn);
  const resolve = useResolveCase(caseUrn);
  const convert = useConvertCareOnly(caseUrn);

  const isResolved = String(overview.data?.status ?? '').toUpperCase() === 'RESOLVED';
  const isCareOnly = overview.data?.rebookingDisabled === true;
  const pax = overview.data?.passengerCount;
  const groups = allocation.data?.groups?.total;
  const rooms = allocation.data?.rooms;
  const rebooking = allocation.data?.rebooking;
  const spend = allocation.data?.spend;

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5" data-testid="case-stat-strip">
      {typeof pax === 'number' ? (
        <Stat
          icon={Users}
          value={t('strip.pax', { count: pax })}
          {...(typeof groups === 'number' ? { detail: t('strip.groups', { count: groups }) } : {})}
        />
      ) : null}

      {/* Re-accommodation progress — only once the engine has produced verdicts.
          NEX-604: use `rebooked` (selected + overridden), NOT `resolved` which includes
          noOptions. A group with NO_OPTIONS was NOT re-accommodated — it has no flight. */}
      {rebooking && rebooking.groups > 0 ? (
        <Stat
          icon={Plane}
          value={t('strip.rebooked', { done: rebooking.rebooked, total: rebooking.groups })}
          {...(rebooking.noOptions > 0
            ? { detail: t('strip.noOptions', { count: rebooking.noOptions }), tone: 'danger' as const }
            : rebooking.pending > 0
              ? { detail: t('strip.rebookingPending', { count: rebooking.pending }) }
              : {})}
        />
      ) : null}

      {/* Rooms — hidden entirely on a case that needs no hotel (a meal-only case).
          Per docs/kpi-definitions.md §2.3: Do NOT display rooms until requiredKnown === true.
          When requiredKnown === false, required is an upper bound, not the actual need.
          @see us3-group/nexa#746 */}
      {rooms && rooms.required > 0 && rooms.requiredKnown !== false ? (
        <Stat
          icon={Hotel}
          value={t('strip.rooms', { done: rooms.booked + rooms.held, total: rooms.required })}
          {...(rooms.held > 0
            ? { detail: t('strip.roomsHeld', { count: rooms.held }) }
            : rooms.outstanding > 0
              ? { detail: t('strip.roomsOutstanding', { count: rooms.outstanding }), tone: 'warn' as const }
              : {})}
        />
      ) : null}

      {/* NEX-603: "Booked: $X" with the held amount as detail — the airline's PRICE
          (commercial-contract §6.13: the BFF prices it). No price card → the amounts
          are null: the tile still shows when the case holds rooms, saying «No
          disponible» — never a 0 that would read as "nothing spent". */}
      {spend && isPriced(spend.bookedPriceUsd) && isPriced(spend.heldPriceUsd) ? (
        spend.bookedPriceUsd > 0 || spend.heldPriceUsd > 0 ? (
          <Stat
            icon={DollarSign}
            value={t('strip.spend', { amount: compactUsd(spend.bookedPriceUsd) })}
            {...(spend.heldPriceUsd > 0
              ? { detail: t('strip.spendHeld', { amount: compactUsd(spend.heldPriceUsd) }) }
              : {})}
          />
        ) : null
      ) : spend && rooms && rooms.booked + rooms.held > 0 ? (
        <Stat icon={DollarSign} value={t('strip.spend', { amount: t(UNPRICED_KEY) })} />
      ) : null}

      {/* Convert to care-only — stops flight rebooking on a case already in
          flight (unresolved PNRs stamped; chosen flights kept; hotels/meals/
          transport untouched). Hidden once the case IS care-only: the server op
          is idempotent, but re-offering a done conversion only invites doubt. */}
      {overview.data && !isCareOnly ? (
        <RoleGate min={Role.OPS_SUPERVISOR} hideWhenDenied>
          <ConfirmDialog
            title={t('careOnly.dialogTitle')}
            description={t('careOnly.dialogDescription')}
            confirmLabel={t('careOnly.confirm')}
            onConfirm={async () => {
              await convert.mutateAsync();
            }}
            trigger={
              <Button
                variant="outline"
                size="sm"
                disabled={convert.isPending}
                className="h-7 gap-1.5 px-2.5 text-xs"
                data-testid="convert-care-only"
              >
                <ConciergeBell className="h-3.5 w-3.5" aria-hidden />
                {t('careOnly.action')}
              </Button>
            }
          />
        </RoleGate>
      ) : null}

      <RoleGate min={Role.ADMIN} hideWhenDenied>
        <ConfirmDialog
          title={t('resolve.dialogTitle')}
          description={t('resolve.dialogDescription')}
          confirmLabel={t('resolve.action')}
          onConfirm={() => resolve.mutateAsync()}
          trigger={
            <Button
              variant={isResolved ? 'outline' : 'default'}
              size="sm"
              disabled={isResolved || resolve.isPending}
              className="h-7 gap-1.5 px-2.5 text-xs"
            >
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
              {isResolved ? t('resolve.done') : t('resolve.action')}
            </Button>
          }
        />
      </RoleGate>
    </div>
  );
}

interface StatProps {
  icon: React.ComponentType<{ className?: string }>;
  value: string;
  detail?: string;
  tone?: 'warn' | 'danger';
}

function Stat({ icon: Icon, value, detail, tone }: StatProps) {
  return (
    <span className="flex items-center gap-1.5 text-xs">
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
      <span className="font-semibold tabular-nums text-foreground">{value}</span>
      {detail ? (
        <span
          className={
            tone === 'danger'
              ? 'text-destructive'
              : tone === 'warn'
                ? 'text-amber-700 dark:text-amber-400'
                : 'text-muted-foreground'
          }
        >
          {detail}
        </span>
      ) : null}
    </span>
  );
}
